/* eslint-disable no-else-return */
import * as Sentry from '@sentry/react';
import Amplify, { PubSub } from 'aws-amplify';
import { AWSIoTProvider } from '@aws-amplify/pubsub';
import awsIot from 'aws-iot-device-sdk';
import {
  IoTDataPlaneClient,
  GetRetainedMessageCommand,
  ListRetainedMessagesCommand,
} from '@aws-sdk/client-iot-data-plane';
import { getCurrentUser, setCurrentUser } from './Utils';
import api from './Api';
import User from '../services/User';

/**
 *  MQTT requests (over WSS)
 *  Encapsulate for better readability / possible migration to another MQTT provider
 */
const Mqtt = {
  deviceClient: null,
  subscribedTopicHandlers: {},

  getEndpoint: () => {
    if (!User.isLoggedIn()) {
      return '';
    }
    if (getCurrentUser().mqtt) {
      return getCurrentUser().mqtt.endpoint;
    } else {
      User.logout().then(() => {
        window.location.reload();
      });
      return 'a3avkx03h47eo8-ats.iot.ap-east-1.amazonaws.com';
    }
  },
  getCredentials: () => {
    if (!User.isLoggedIn()) {
      return {};
    }
    if (getCurrentUser().mqtt) {
      return getCurrentUser().mqtt.credentials;
    } else {
      return {
        accessKeyId: getCurrentUser().accessKeyId,
        secretAccessKey: getCurrentUser().secretAccessKey,
        sessionToken: getCurrentUser().sessionToken,
      };
    }
  },
  //  Note: Login / Set credentials through Auth before subscribing to certain AWS IoT Core endpoint
  configure: (region, endpoint) => {
    const iotProvider = new AWSIoTProvider({
      aws_pubsub_region: region,
      aws_pubsub_endpoint: endpoint,
    });
    Amplify.addPluggable(iotProvider);
  },
  initDeviceClient: (onConnect) => {
    Mqtt.deviceClient = awsIot.device({
      host: Mqtt.getEndpoint(),
      protocol: 'wss',
      accessKeyId: Mqtt.getCredentials().accessKeyId,
      secretKey: Mqtt.getCredentials().secretAccessKey,
      sessionToken: Mqtt.getCredentials().sessionToken,
    });
    Mqtt.deviceClient.on('connect', () => {
      onConnect();
    });
    Mqtt.deviceClient.on('message', (messageTopic, payload) => {
      let handler = Mqtt.subscribedTopicHandlers[messageTopic]; //  TODO Check + and # of Mqtt.subscribedTopicHandlers keys
      if (!!handler) {
        handler({
          topic: messageTopic,
          value: JSON.parse(payload.toString()),
        });
      }
    });
    Mqtt.deviceClient.on('error', (err) => Sentry.captureException(err));
    Mqtt.deviceClient.on('reconnect', async () => {
      try {
        if (new Date(Mqtt.getCredentials().expiration) < new Date()) {
          //  Refresh token if expired
          let response = await api.post('/mqtt/refresh_token', {
            siteCode: process.env.REACT_APP_SITECODE,
          });
          let user = getCurrentUser(),
            credentials;
          user.mqtt = { region: user.mqtt.region, ...response.data.data };
          credentials = user.mqtt.credentials;
          Mqtt.deviceClient.updateWebSocketCredentials(
            credentials.accessKeyId,
            credentials.secretAccessKey,
            credentials.sessionToken,
            credentials.expiration
          );
          setCurrentUser(user);
        }
      } catch (e) {
        User.logout().then(() => {
          window.location.reload();
        });
      }
    });
  },
  subscribe: (subscribeTopic, handler) => {
    const crendtials = Mqtt.getCredentials;
    if (
      !crendtials.accessKeyId ||
      !crendtials.secretAccessKey ||
      !crendtials.sessionToken
    ) {
      //  Refresh token
      api
        .post('/mqtt/refresh_token', {
          siteCode: process.env.REACT_APP_SITECODE,
        })
        .then((response) => {
          let user = getCurrentUser(),
            credentials;
          user.mqtt = { region: user.mqtt.region, ...response.data.data };
          credentials = user.mqtt.credentials;
          Mqtt.deviceClient.updateWebSocketCredentials(
            credentials.accessKeyId,
            credentials.secretAccessKey,
            credentials.sessionToken,
            credentials.expiration
          );
          setCurrentUser(user);
        });
    }
    if (!Mqtt.deviceClient) {
      Mqtt.initDeviceClient(() => {
        //  Subscribe
        Mqtt.deviceClient.subscribe(subscribeTopic, {});
        Mqtt.subscribedTopicHandlers[subscribeTopic] = handler;
      });
    } else {
      //  Subscribe
      Mqtt.deviceClient.subscribe(subscribeTopic, {});
      Mqtt.subscribedTopicHandlers[subscribeTopic] = handler;
    }
    return {
      unsubscribe: () => {
        //  Remove topic handler
        delete Mqtt.subscribedTopicHandlers[subscribeTopic];
      },
    };
  },
  publish: async (topic, message, qos, retain) => {
    console.log(`MQTT publish: ${topic}`, message);
    const options = {
      qos: qos || 0,
      retain: retain || false,
    };
    if (!Mqtt.deviceClient) {
      Mqtt.initDeviceClient(() => {
        Mqtt.deviceClient.publish(topic, JSON.stringify(message), options);
      });
    } else {
      Mqtt.deviceClient.publish(topic, JSON.stringify(message), options);
    }
  },

  getRetainedMessage: async (topic, config) => {
    const user = getCurrentUser();

    const client = new IoTDataPlaneClient({
      region: 'ap-east-1',
      credentials: {
        accessKeyId: user.mqtt.credentials.accessKeyId,
        secretAccessKey: user.mqtt.credentials.secretAccessKey,
        sessionToken: user.mqtt.credentials.sessionToken,
      },
    });
    const command = new GetRetainedMessageCommand({ topic });
    return client.send(command);
  },

  listRetainedMessages: async (topic, maxResults, config) => {
    const user = getCurrentUser();

    const client = new IoTDataPlaneClient({
      region: 'ap-east-1',
      credentials: {
        accessKeyId: user.mqtt.credentials.accessKeyId,
        secretAccessKey: user.mqtt.credentials.secretAccessKey,
        sessionToken: user.mqtt.credentials.sessionToken,
      },
    });
    const command = new ListRetainedMessagesCommand({
      topic,
      maxResults,
    });
    return client.send(command);
  },
};

export default Mqtt;
